import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { GECKO_CONSTS, GECKO_UTILS, GeckoFields } from 'ui';
import AgentTeamSelect from 'components/AgentTeamSelect';
import BlockField from 'components/BlockField';
import ReplyBoxField from 'components/ReplyBoxField';
import {
  buildContactGroup,
  buildSelectedChannelGroup,
} from 'services/template-tags';
import { BotTagManagerField } from '../BotTagManager';
import './index.scss';

const hasOnlyEmptyValues = (values) => {
  return values.some((val) => val === '');
};

function ActionWrapper(props) {
  const { icon, name, description, hasValue, children } = props;
  const [expanded, setExpanded] = useState(false);

  const toggleExpansion = () => {
    setExpanded(!expanded);
  };

  const isActive = expanded || hasValue;
  const actionIcon = isActive ? 'far fa-check' : 'far fa-plus';

  const idPrefix = `ActionWrapper_${name.replace(/\s/g, '-')}`;
  const toggleButtonID = `${idPrefix}_expand_button`;
  const expandableContentID = `${idPrefix}_content`;

  return (
    <div className="SkillWorkflowAction-ActionWrapper">
      <div
        className="SkillWorkflowAction-ActionWrapper-header"
        id={toggleButtonID}
        role="button"
        aria-expanded={expanded}
        aria-controls={expandableContentID}
        aria-label={name}
        onClick={toggleExpansion}
      >
        <div className="SkillWorkflowAction-ActionWrapper-icon">
          <i className={icon} />
        </div>
        <div className="SkillWorkflowAction-ActionWrapper-title">
          <div className="SkillWorkflowAction-ActionWrapper-title-name">
            {name}
          </div>
          <div className="SkillWorkflowAction-ActionWrapper-title-description">
            {description}
          </div>
        </div>
        <div className="SkillWorkflowAction-ActionWrapper-action-icon">
          <i className={actionIcon} />
        </div>
      </div>
      <div
        className="SkillWorkflowAction-ActionWrapper-content"
        hidden={!expanded}
        role="region"
        id={expandableContentID}
        aria-labelledby={toggleButtonID}
      >
        {children}
      </div>
    </div>
  );
}
ActionWrapper.propTypes = {
  icon: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.element,
  hasValue: PropTypes.bool,
};

function AssignAgentTeamAction(props) {
  const { value = [], onChange = () => {} } = props;

  const fields = () => {
    return [
      {
        key: 'value',
        label: 'Choose which agent/team to assign the conversation to',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <AgentTeamSelect />,
      },
    ];
  };

  const changeHandler = (_key, value) => {
    onChange(value);
  };
  return (
    <div className="SkillWorkflowAction-AssignAgentTeamAction">
      <ActionWrapper
        icon="far fa-users"
        name="Assign conversation to agent/team"
        description="This will assign the conversation to the selected agent/team, this will NOT stop the bot handling the conversation."
        hasValue={value.length}
      >
        <GeckoFields
          values={{ value: value }}
          fields={fields()}
          handler={changeHandler}
        />
      </ActionWrapper>
    </div>
  );
}
AssignAgentTeamAction.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
};

function TriggerMessageAction(props) {
  const { value = [''], onChange = () => {} } = props;

  const templateContext = [
    buildContactGroup(),
    buildSelectedChannelGroup({
      channel: null,
      showChannelQueuePosition: true,
    }),
  ];

  const fields = () => {
    return [
      {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        component: (
          <BlockField
            addButton={{
              type: 'default',
              name: 'Add Another Message',
              icon: 'far fa-plus',
            }}
            isAddButtonDisabled={hasOnlyEmptyValues(value)}
            field={{
              label: 'Compose message',
              type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
              component: (
                <ReplyBoxField
                  compileTemplateTags={false}
                  templateContext={templateContext}
                  meta={{ mail_merge: { show: true } }}
                />
              ),
            }}
          />
        ),
      },
    ];
  };
  const changeHandler = (_key, value) => {
    onChange(value);
  };
  return (
    <div className="SkillWorkflowAction-TriggerMessageAction">
      <ActionWrapper
        icon="far fa-comments"
        name="Trigger a message to the contact"
        hasValue={value.filter((val) => val !== '').length}
      >
        <GeckoFields
          values={{ value: value }}
          fields={fields()}
          handler={changeHandler}
        />
      </ActionWrapper>
    </div>
  );
}
TriggerMessageAction.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
};

function AddNoteAction(props) {
  const { value = [''], onChange = () => {} } = props;

  const fields = () => {
    return [
      {
        key: 'value',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        component: (
          <BlockField
            addButton={{
              preset: 'default',
              name: 'Add Another Note',
              icon: 'far fa-plus',
            }}
            isAddButtonDisabled={hasOnlyEmptyValues(value)}
            field={{
              label: 'Compose note',
              type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
              component: (
                <ReplyBoxField
                  compileTemplateTags={false}
                  meta={{ mail_merge: { show: false } }}
                />
              ),
            }}
          />
        ),
      },
    ];
  };

  const changeHandler = (_key, value) => {
    onChange(value);
  };

  const isValueFilled = (val) => val !== '';
  const hasValue = value.filter(isValueFilled).length !== 0;

  return (
    <div className="SkillWorkflowAction-AddNoteAction">
      <ActionWrapper
        icon="far fa-sticky-note"
        name="Add a note to the contact"
        description="Notes are only visible to other agents and not to the contact themselves."
        hasValue={hasValue}
      >
        <GeckoFields
          values={{ value: value }}
          fields={fields()}
          handler={changeHandler}
        />
      </ActionWrapper>
    </div>
  );
}
AddNoteAction.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
};

function AddConversationTagAction(props) {
  const { value = [], onChange = () => {}, tags = [] } = props;

  const fields = () => {
    return [
      {
        key: 'tags_value',
        label: 'Add the following tags to the conversation',
        type: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        options: tags.map((tag) => {
          return { label: tag.name, value: tag.id };
        }),
      },
    ];
  };
  const changeHandler = (_key, value) => {
    onChange(value);
  };
  return (
    <div className="SkillWorkflowAction-AddConversationTagAction">
      <ActionWrapper
        icon="far fa-tags"
        name="Assign a tag to the conversation"
        hasValue={value.length}
      >
        <GeckoFields
          values={{ tags_value: value }}
          fields={fields()}
          handler={changeHandler}
        />
      </ActionWrapper>
    </div>
  );
}
AddConversationTagAction.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
};

function EndConversationWithBotAction(props) {
  const { value = false, onChange = () => {} } = props;

  const fields = () => {
    return [
      {
        key: 'end_conversation_value',
        label: 'End Bot Conversation',
        type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
      },
    ];
  };
  const changeHandler = (_key, value) => {
    onChange(value);
  };
  return (
    <div className="SkillWorkflowAction-EndConversationWithBotAction">
      <ActionWrapper
        icon="far fa-comment-slash"
        name="End Conversation with Bot"
        description="This will prevent the bot from responding to incoming messages and disable the bot automatically closing conversations after a period of inactivity."
        hasValue={value}
      >
        <GeckoFields
          values={{ end_conversation_value: value }}
          fields={fields()}
          handler={changeHandler}
        />
      </ActionWrapper>
    </div>
  );
}
EndConversationWithBotAction.propTypes = {
  value: PropTypes.bool,
  onChange: PropTypes.func,
};

function SkillWorkflowAction(props) {
  const defaultValue = {
    name: '',
    internal_skill: 'WORKFLOW_ACTION',
    config: {},
    outro_actions: [],
  };
  const { value = defaultValue, onChange = () => {} } = props;

  const tags = useSelector((state) =>
    state.tags.filter((tag) => !tag.ai_generated)
  );

  useEffect(() => {
    onChange(value);
  }, []);

  const parseToExternalFormat = (internalSkill) => {
    const skill = { ...internalSkill };
    const outro_actions = [];
    if (skill.assignments) {
      outro_actions.push({
        type: 'assign_conversation',
        value: skill.assignments,
      });
      delete skill.assignments;
    }
    if (skill.messages && skill.messages.length) {
      outro_actions.push({
        type: 'send_message',
        value: skill.messages,
      });
      delete skill.messages;
    }
    if (skill.notes && skill.notes.length) {
      outro_actions.push({
        type: 'contact_note',
        value: skill.notes,
      });
      delete skill.notes;
    }
    if (skill.tags) {
      outro_actions.push({
        type: 'conversation_tag',
        value: skill.tags,
      });
      delete skill.tags;
    }
    if (skill.end_conversation_with_bot) {
      outro_actions.push({
        type: 'end_conversation_with_bot',
        value: skill.end_conversation_with_bot,
      });
      delete skill.end_conversation_with_bot;
    }
    return { ...skill, outro_actions };
  };

  const parseToInternalFormat = (externalSkill) => {
    const skill = { ...externalSkill };
    (skill.outro_actions || []).forEach((action) => {
      if (action.type === 'assign_conversation') {
        skill.assignments = (skill.assignments || []).concat(action.value);
      }
      if (action.type === 'send_message') {
        skill.messages = (skill.messages || []).concat(action.value);
      }
      if (action.type === 'contact_note') {
        skill.notes = (skill.notes || []).concat(action.value);
      }
      if (action.type === 'conversation_tag') {
        skill.tags = (skill.tags || []).concat(action.value);
      }
      if (action.type === 'end_conversation_with_bot') {
        skill.end_conversation_with_bot = action.value;
      }
    });
    if (!skill.messages || skill.messages === []) {
      skill.messages = [''];
    }
    if (!skill.notes || skill.notes === []) {
      skill.notes = [''];
    }
    skill.outro_actions = [];
    return skill;
  };

  const fields = () => {
    return [
      {
        key: '',
        label: 'Configure the Workflow Action Skill',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        className: 'BotTabEditContainer-section-heading',
        component: <div style={{ display: 'none' }}></div>,
      },
      {
        key: 'name',
        label: 'Skill Title',
        required: true,
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_TEXT,
      },
      {
        key: 'bot_tag_ids',
        label: 'Skill Tags',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        width: '100%',
        component: <BotTagManagerField item={value} />,
      },
      {
        key: 'assignments',
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <AssignAgentTeamAction />,
      },
      {
        key: 'messages',
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <TriggerMessageAction />,
      },
      {
        key: 'notes',
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <AddNoteAction />,
      },
      {
        key: 'tags',
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <AddConversationTagAction tags={tags} />,
      },
      {
        key: 'end_conversation_with_bot',
        width: '100%',
        type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
        component: <EndConversationWithBotAction />,
      },
    ];
  };

  const handler = (fieldKey, fieldValue) => {
    const internalSkill = parseToInternalFormat(value);
    const skill = GECKO_UTILS.setNestedValue(
      internalSkill,
      fieldKey,
      fieldValue
    );
    const externalFormatSkill = parseToExternalFormat(skill);
    onChange(externalFormatSkill);
  };

  return (
    <div className="SkillWorkflowAction">
      <div className="row">
        <GeckoFields
          values={parseToInternalFormat(value)}
          fields={fields()}
          handler={handler}
        />
      </div>
    </div>
  );
}
SkillWorkflowAction.propTypes = {
  value: PropTypes.shape({
    name: PropTypes.string,
    internal_skill: PropTypes.oneOf(['WORKFLOW_ACTION']),
    bot_tag_ids: PropTypes.arrayOf(PropTypes.string),
    config: PropTypes.object,
    outro_actions: PropTypes.arrayOf(PropTypes.object),
  }),
  onChange: PropTypes.func,
};

export default SkillWorkflowAction;
